import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { principles } from '@/constants/home'

export const Principles = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        // Each row is its own little performance: rule draws, number fades,
        // heading words rise, body copy follows.
        select('[data-principle]').forEach((row) => {
            const trigger = { trigger: row, start: 'top 82%' }

            gsap.from(row.querySelector('[data-principle-rule]'), {
                scaleX: 0,
                transformOrigin: 'left center',
                duration: 1.1,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(row.querySelector('[data-principle-number]'), {
                opacity: 0,
                y: 14,
                duration: 0.8,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(row.querySelectorAll('[data-principle-title] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1,
                stagger: 0.05,
                ease: 'nuvea',
                delay: 0.08,
                scrollTrigger: trigger
            })

            gsap.from(row.querySelector('[data-principle-body]'), {
                opacity: 0,
                y: 22,
                duration: 0.9,
                delay: 0.2,
                ease: 'nuvea',
                scrollTrigger: trigger
            })
        })
    })

    return (
        <section
            ref={scope}
            id='principles'
            className='shell-x mx-auto w-full max-w-[1760px] pb-24 md:pb-32'
        >
            <ul>
                {principles.map((principle, index) => (
                    <li key={principle.number} data-principle='' className='relative pt-8 pb-14 md:pt-10 md:pb-20'>
                        <span
                            data-principle-rule=''
                            aria-hidden='true'
                            className='absolute inset-x-0 top-0 block h-px bg-stone-line'
                        />

                        <div
                            className={cn(
                                'grid grid-cols-1 gap-6 md:grid-cols-[7rem_1fr_1fr] md:gap-10 lg:gap-16',
                                // Alternate rows step inward, so the list reads as a staircase.
                                index % 2 === 1 && 'lg:pl-[8%]'
                            )}
                        >
                            <p
                                data-principle-number=''
                                className='type-h3 text-sage-deep tabular-nums'
                            >
                                {principle.number}
                            </p>

                            <SplitWords
                                data-principle-title=''
                                as='h2'
                                lines={principle.title}
                                className='type-h2 max-w-[16ch] text-ink'
                            />

                            <p
                                data-principle-body=''
                                className='type-copy max-w-[52ch] text-slate-soft md:self-end'
                            >
                                {principle.body}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    )
}
